import { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'

const VISUALIZER_SRC = '/india-job-market-visualizer/index.html'
const FALLBACK_HEIGHT = 960

export default function IndiaJobMarketVisualizerPage() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const [iframeHeight, setIframeHeight] = useState(FALLBACK_HEIGHT)

  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current
    const doc = iframe?.contentDocument

    if (!iframe || !doc) return

    const nextHeight = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.body?.offsetHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
      doc.documentElement?.offsetHeight ?? 0,
      FALLBACK_HEIGHT
    )

    setIframeHeight(nextHeight)
  }, [])

  const handleLoad = useCallback(() => {
    cleanupRef.current?.()
    cleanupRef.current = null

    const iframe = iframeRef.current
    const doc = iframe?.contentDocument
    const win = iframe?.contentWindow

    if (!iframe || !doc || !win) return

    const update = () => {
      window.requestAnimationFrame(syncIframeHeight)
    }

    update()
    void doc.fonts?.ready?.then(update).catch(() => {})

    const observer = new ResizeObserver(update)

    if (doc.body) observer.observe(doc.body)
    if (doc.documentElement) observer.observe(doc.documentElement)

    win.addEventListener('resize', update)

    cleanupRef.current = () => {
      observer.disconnect()
      win.removeEventListener('resize', update)
    }
  }, [syncIframeHeight])

  useEffect(() => {
    return () => {
      cleanupRef.current?.()
    }
  }, [])

  return (
    <div style={{ position: 'relative', zIndex: 20, minHeight: '100vh', background: '#FAFAF7' }}>
      <Helmet>
        <title>India Job Market Visualizer | Staying Ahead</title>
        <meta
          name="description"
          content="Interactive treemap of India's workforce with Digital AI Exposure, wages, formal-sector share, growth outlook, and more."
        />
        <link rel="canonical" href="https://stayingahead.club/india-job-market-visualizer" />
      </Helmet>

      <iframe
        ref={iframeRef}
        title="India Job Market Visualizer"
        src={VISUALIZER_SRC}
        onLoad={handleLoad}
        style={{
          display: 'block',
          width: '100%',
          minHeight: '100vh',
          height: `${iframeHeight}px`,
          border: 0,
          background: '#FAFAF7',
        }}
      />
    </div>
  )
}
